import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Container,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Collapse,
  Menu,
  MenuItem,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import { ThemeToggle } from './ThemeToggle';
import unlockLevelsJson from '../data/unlock_levels.json';
import unitsData from '../data/units_merged.json';
import NavHeader from './NavHeader';
import ModuleDropdown from './ModuleDropdown';
import HomeIcon from './icons/HomeIcon';
import DocumentIcon from './icons/DocumentIcon';
import ProfileIcon from './icons/ProfileIcon';
import LogoutIcon from './icons/LogoutIcon';
import IntranetIcon from './icons/IntranetIcon';
import TimeClockIcon from './icons/TimeClockIcon';
import QuizIcon from './icons/QuizIcon';
import FaqIcon from './icons/FaqIcon';
import VideoIcon from './icons/VideoIcon';
import RunningAvatar from './RunningAvatar';
import PaletteIcon from './icons/PaletteIcon';
import { useTheme as useThemeContext } from '../contexts/ThemeContext';
import { tokens } from '../theme/tokens';
import PortalPageTemplate from './shared/PortalPageTemplate';

const MODULES = [
  { id: 'M1', title: 'M1 Lüftung' },
  { id: 'M2', title: 'M2 Wärmeerzeugung' },
  { id: 'M3', title: 'M3 Kälte' },
  { id: 'M4', title: 'M4 Regelung' },
  { id: 'M5', title: 'M5 Bus Systeme' },
  { id: 'M6', title: 'M6 Geräte' },
  { id: 'M10', title: 'M10 Funktionen' },
  { id: 'M50', title: 'M50 Step-by-Step' },
  { id: 'M98', title: 'Hilfe/FAQ' },
  { id: 'M99', title: 'Tools/Highscore' },
];

type AppShellProps = { children?: React.ReactNode; fullBleed?: boolean };

const AppShell: React.FC<AppShellProps> = ({ children, fullBleed = true }) => {
  const muiTheme = useTheme();
  const isSmallScreen = useMediaQuery(muiTheme.breakpoints.down('md'));
  const { darkMode } = useThemeContext();
  const {
    user,
    logout,
    totalScore,
    userLevel,
    canManageUpdates,
    canAccessAdmin,
    canViewRolesLegend,
    canEditRolesLegend,
    canManageThemeSettings,
    canViewDocuments,
    canAnswerQuizzes,
    canEditContent
  } = useAuth();
  const navigate = useNavigate();
  const canAccessAdminMenu = canAccessAdmin || canManageUpdates || canViewRolesLegend || canEditRolesLegend || canManageThemeSettings;

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [expanded, setExpanded] = React.useState<Record<string, boolean>>({});

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleNavigate = (path: string) => {
    navigate(path);
    handleMenuClose();
  };
  const handleExternalLink = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    handleMenuClose();
  };
  const handleLogout = () => {
    logout();
    handleMenuClose();
  };

  const unlockLevels: Record<string, number> = (unlockLevelsJson as any) || {};
  const modulesFromData = React.useMemo(() => {
    try {
      return Object.keys((unitsData as any) || {}).sort();
    } catch (e) {
      return MODULES.map((m) => m.id);
    }
  }, []);

  const toggleModule = (modId: string) => setExpanded((prev) => ({ ...prev, [modId]: !prev[modId] }));

  // minimal erforderliches Level je Modul bestimmen
  const moduleRequired = (modId: string): number => {
    const keys = Object.keys(unlockLevels).filter((k) => k.startsWith(modId + '_') || k.startsWith(modId));
    if (keys.length === 0) return 0;
    return keys.reduce((min, k) => Math.min(min, unlockLevels[k] ?? 0), Number.MAX_SAFE_INTEGER) || 0;
  };
  return (
    <PortalPageTemplate darkMode={darkMode}>
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Easter Egg: Laufender Avatar - aktuell deaktiviert */}
      <RunningAvatar delaySeconds={2} intervalSeconds={5} enabled={false} />
      
      <AppBar
        position="static"
        color="inherit"
        sx={{
          backgroundColor: darkMode ? tokens.colors.dark.surface : tokens.colors.light.surface,
          borderBottom: `1px solid ${darkMode ? tokens.colors.dark.border : tokens.colors.light.border}`
        }}
      >
        <Toolbar>
          <Box sx={{ mr: 1 }}>
            <ModuleDropdown />
          </Box>

          <Box
            component="img"
            src="/assets/se_logo_klein.jpg"
            alt="SE Logo"
            sx={{ height: 36, mr: 1.5 }}
          />

          <Box sx={{ flexGrow: 1 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 2 }}>
            <Typography variant="caption" sx={{ display: { xs: 'none', sm: 'block' } }}>
              {user?.email}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {totalScore} Pkt
            </Typography>
          </Box>

          {isSmallScreen ? (
            <>
              <IconButton onClick={handleMenuOpen} color="primary">
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem
                  disableRipple
                  disableTouchRipple
                  sx={{ cursor: 'default' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <ThemeToggle />
                </MenuItem>
                <MenuItem onClick={() => handleNavigate('/')}> 
                  <ListItemIcon><HomeIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Start</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleNavigate('/profile')}>
                  <ListItemIcon><ProfileIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Profil</ListItemText>
                </MenuItem>
                {canViewDocuments && (
                  <MenuItem onClick={() => handleNavigate('/units/m98_u5_dokumente')}>
                    <ListItemIcon><DocumentIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Dokumente</ListItemText>
                  </MenuItem>
                )}
                {canAnswerQuizzes && (
                  <MenuItem onClick={() => handleNavigate('/quizzes')}>
                    <ListItemIcon><QuizIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Quizzes</ListItemText>
                  </MenuItem>
                )}
                <MenuItem onClick={() => handleNavigate('/faq')}>
                  <ListItemIcon><FaqIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>FAQ</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleNavigate('/videos')}>
                  <ListItemIcon><VideoIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Videos</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleExternalLink('https://intranet.se-urbach.de/')}>
                  <ListItemIcon><IntranetIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Intern</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleExternalLink('https://zeus.se-urbach.de/')}>
                  <ListItemIcon><TimeClockIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Zeiterfassung</ListItemText>
                </MenuItem>
                {canAccessAdminMenu && (
                  <>
                    <MenuItem onClick={() => handleNavigate('/admin')}>
                      <ListItemIcon><AdminPanelSettingsIcon fontSize="small" /></ListItemIcon>
                      <ListItemText>Admin</ListItemText>
                    </MenuItem>
                    {canManageUpdates && (
                      <MenuItem onClick={() => handleNavigate('/admin/updates')}>
                        <ListItemIcon><DescriptionIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Updates</ListItemText>
                      </MenuItem>
                    )}
                    {canViewRolesLegend && (
                      <MenuItem onClick={() => handleNavigate('/admin/roles')}>
                        <ListItemIcon><AdminPanelSettingsIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Rollen</ListItemText>
                      </MenuItem>
                    )}
                    {canManageThemeSettings && (
                      <MenuItem onClick={() => handleNavigate('/admin/theme')}>
                        <ListItemIcon><PaletteIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Design</ListItemText>
                      </MenuItem>
                    )}
                  </>
                )}
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Logout</ListItemText>
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <ThemeToggle />
              <Tooltip title="Start">
                <IconButton color="primary" component={RouterLink} to="/">
                  <HomeIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Profil">
                <IconButton color="primary" component={RouterLink} to="/profile">
                  <ProfileIcon />
                </IconButton>
              </Tooltip>
              {canViewDocuments && (
                <Tooltip title="Dokumente">
                  <IconButton color="primary" component={RouterLink} to="/units/m98_u5_dokumente">
                    <DocumentIcon />
                  </IconButton>
                </Tooltip>
              )}
              {canAnswerQuizzes && (
                <Tooltip title="Quizzes">
                  <IconButton color="primary" component={RouterLink} to="/quizzes">
                    <QuizIcon />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="FAQ">
                <IconButton color="primary" component={RouterLink} to="/faq">
                  <FaqIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Videos">
                <IconButton color="primary" component={RouterLink} to="/videos">
                  <VideoIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Intern">
                <IconButton color="primary" onClick={() => window.open('https://intranet.se-urbach.de/', '_blank')}>
                  <IntranetIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Zeiterfassung">
                <IconButton color="primary" onClick={() => window.open('https://zeus.se-urbach.de/', '_blank')}>
                  <TimeClockIcon />
                </IconButton>
              </Tooltip>
              {canAccessAdminMenu && (
                <>
                  <Tooltip title="Admin">
                    <IconButton color="primary" component={RouterLink} to="/admin">
                      <AdminPanelSettingsIcon />
                    </IconButton>
                  </Tooltip>
                  {canManageUpdates && (
                    <Tooltip title="Updates">
                      <IconButton color="primary" component={RouterLink} to="/admin/updates">
                        <DescriptionIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canViewRolesLegend && (
                    <Tooltip title="Rollen">
                      <IconButton color="primary" component={RouterLink} to="/admin/roles">
                        <AdminPanelSettingsIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canManageThemeSettings && (
                    <Tooltip title="Design">
                      <IconButton color="primary" component={RouterLink} to="/admin/theme">
                        <PaletteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              )}
              <Tooltip title="Logout">
                <IconButton color="error" onClick={logout}>
                  <LogoutIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Drawer mit allen Modulen/Units (hierarchie) */}
      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 320 }} role="presentation" onKeyDown={() => setDrawerOpen(false)}>
          <NavHeader />
          <List>
            <ListItemButton component={RouterLink} to="/">
              <ListItemIcon><FolderIcon /></ListItemIcon>
              <ListItemText primary="Start" />
            </ListItemButton>

            {modulesFromData.map((modId) => {
              const modObj = (unitsData as any)[modId] || {};
              const requiredForModule = moduleRequired(modId);
              const allowedModule = canEditContent || userLevel >= requiredForModule;
              const units = Object.keys(modObj || {}).sort();
              return (
                <React.Fragment key={modId}>
                  <ListItemButton onClick={() => toggleModule(modId)} disabled={!allowedModule}>
                    <ListItemIcon><FolderIcon /></ListItemIcon>
                    <ListItemText
                      primary={(() => {
                        const found = MODULES.find((m) => m.id === modId);
                        return found ? found.title : modId;
                      })()}
                      secondary={allowedModule ? undefined : `Benötigt Level ${requiredForModule}`}
                    />
                    {expanded[modId] ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>

                  <Collapse in={!!expanded[modId]} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {units.map((unitKey) => {
                        const required = unlockLevels[unitKey] ?? 0;
                        const allowed = canEditContent || userLevel >= required;
                        return (
                          <ListItemButton
                            key={unitKey}
                            sx={{ pl: 4 }}
                            component={RouterLink}
                            to={`/modules/${modId}/${unitKey}`}
                            disabled={!allowed}
                            title={!allowed ? `Benötigt Level ${required}` : undefined}
                          >
                            <ListItemIcon><DescriptionIcon /></ListItemIcon>
                            <ListItemText primary={((unitsData as any)[modId]?.[unitKey]?.title) || unitKey} />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Collapse>
                </React.Fragment>
              );
            })}
          </List>
        </Box>
      </Drawer>

      <Container
        sx={{
          flex: 1,
          py: fullBleed ? 0 : 3,
          display: fullBleed ? 'flex' : 'block',
          flexDirection: fullBleed ? 'column' : undefined,
          px: fullBleed ? 0 : undefined
        }}
        disableGutters={fullBleed}
        maxWidth={fullBleed ? false : 'lg'}
      >
        {children ?? (
          <Box>
            <Typography variant="h5" gutterBottom>
              Willkommen!
            </Typography>
            <Typography variant="body1" gutterBottom>
              Wähle ein Modul aus dem Menü (oben links) oder nutze die Quicklinks in der oberen Leiste.
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
    </PortalPageTemplate>
  );
};

export default AppShell;

